import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting, useConfig } from "@nosto/preact"
import { getProductImageSrc, productImagePlaceholder } from "../helpers"
import { useEffect, useState } from "preact/hooks"


export default function Product({ product }) {
    const { format } = useCurrencyFormatting()
    const { shopifyStoreSettings } = useConfig()
    const { showSecondaryImage, showPrice, showDescription, imageFallback, showPersonalizedBadge, showSoldOutBadge, showSingleCustomBadge, showSaleBadge } = shopifyStoreSettings?.product || {}

    const description = product.customFields?.find(field => field.key === 'attribute-product_description_text')?.value
    const available_options = product.customFields?.find(field => field.key === 'attribute-available_options')?.value
    const hasAlternateImage = product.alternateImageUrls && product.alternateImageUrls.length > 0

    const tags = [
        product.onSale
            ? {
                label: "Sale",
                class: "ns-badge ns-badge-green-outline ns-d-inline-block ns-text-align-center"
              }
            : undefined
    ].filter(v => !!v)

    const handleImageError = (e) => {
        if (imageFallback && e.target.src !== imageFallback) {
            e.target.src = imageFallback;
        } else if (e.target.src !== productImagePlaceholder) {
            e.target.src = productImagePlaceholder;
        }
    };

    const badgesRaw = product.customFields?.find(f => f.key === 'custom-badges')?.value
    const [metaobjects, setMetaobjects] = useState([])
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!badgesRaw) return
        const ids = Array.isArray(badgesRaw) ? badgesRaw : JSON.parse(badgesRaw)
        if (!ids.length) return
      
        const fetchOne = (id) =>
          fetch("https://9aee91-50.myshopify.com/api/2025-10/graphql.json", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": "b91a095021645b09116bc0dd2662d044"
            },
            body: JSON.stringify({
              query: `
                query GetMetaobjectById($id: ID!) {
                  node(id: $id) {
                    ... on Metaobject {
                      id
                      fields { key value }
                    }
                  }
                }
              `,
              variables: { id }
            }),
          })
            .then(r => r.json())
            .then(d => d?.data?.node ?? null)
            Promise.all(ids.map(fetchOne))
            .then(list => setMetaobjects(list.filter(Boolean)))
            .catch(e => { if (e.name !== 'AbortError') setError(e) })
    }, [badgesRaw])

    function renderMetaBadges(metaobjects) {
        return metaobjects.map((mo) => {
            const F = Object.fromEntries((mo?.fields || []).map(f => [f.key, f.value]))
            const text = F.text || ''
            const bg = F.background_color || ''
            const color = F.text_color || ''
        
            return (
            <span
                key={mo.id}
                title={text}
                class="text-t-brand-foreground-secondary bg-t-background flex justify-center items-center caption font-[350] p-2xs rounded-sm lg:text-body lg:font-[400] lg:p-xs"
                style={{ background: bg, color:color }}
                aria-label={`Badge: ${text}`}
            >
                {text}
            </span>
            )
        })
    }      
    console.log(product)
    return (
        <SerpElement
            as="a"
            hit={product}
            ariaLabel={`Product ${product.name}`}
            class="ns-product ns-border-box ns-clickable ns-text-undecorated text-t-brand-foreground-quaternary transition-[color] duration-300 ease-out hover:text-t-brand-foreground-secondary relative"
        >   
            <div class="relative aspect-square">
                <img
                    class="absolute inset-0 ns-w-100 ns-h-100 ns-object-cover object-center transition duration-300"
                    src={getProductImageSrc(product)}
                    alt={product.name}
                    width="600"
                    height="400"
                    onError={handleImageError}
                />
                {showSecondaryImage && hasAlternateImage ? (
                    <img
                        class="absolute inset-0 block ns-w-100 ns-h-100 ns-object-cover object-center opacity-0 transition duration-300"
                        src={product.alternateImageUrls[0]}
                        alt={product.name}
                        width="600"
                        height="400"
                        onError={handleImageError}
                    />
                ): null}
            </div>
            <div class="ns-relative ns-d-flex ns-flex-column gap-xs py-sm ns-px-xs ns-py-l-sm ns-px-l-0" data-nosto-element="product">
                {tags.length > 0 ? (
                    <div class="ns-tags ns-absolute ns-mb-1">
                        {tags.map(v => (
                            <span key={v.label} class={v.class}>{v.label}</span>
                        ))}
                    </div>
                ): null}
                <div class="ns-d-flex ns-flex-column ns-align-items-start ns-gap-xs ns-flex-l-row ns-justify-content-l-between ns-gap-l-7">
                    <h3 class="ns-clipped ns-text-four-lines ns-text-md-three-lines h6 font-medium tracking-[0.12em] text-t-brand-foreground lg:leading-[1.2] lg:tracking-[0.16em]">{product.name}</h3>
                    {showPrice && product.price > 0 ? (
                        <div class="inline-flex items-center justify-start gap-1.5 body-l" aria-label="Price">
                            <span class="body-l text-t-brand-foreground">{format(product.price)}</span>
                            {product.listPrice && product.listPrice > product.price && (
                                <span class="body-l line-through text-t-brand-foreground-secondary">
                                    {format(product.listPrice)}
                                </span>
                            )}
                        </div>
                    ): null}
                </div>
                {showDescription && description ? (
                    <p class="hidden transition-[color] duration-300 ease-out lg:block lg:body-l lg:text-t-brand-foreground-quaternary">
                        {description}
                    </p>
                ): null}
            </div>
            {available_options ? (
            <p class="text-body text-t-brand-foreground-quaternary group-hover/product-card:text-t-brand-foreground !no-underline transition-[color] duration-300 ease-out lg:body-l">{available_options}</p>
            ): null}
            <div class="flex flex-wrap gap-2 absolute top-[0.3125rem] left-[0.3125rem] right-2 z-1 lg:top-2.5 lg:left-2.5">
                {(showPersonalizedBadge && metaobjects?.length > 0) && renderMetaBadges(metaobjects)}

                {showSoldOutBadge && product.availability != "InStock" && (
                    <span  class="text-t-brand-foreground-secondary bg-t-background flex justify-center items-center caption font-[350] p-2xs rounded-sm lg:text-body lg:font-[400] lg:p-xs">
                        Sold out
                    </span>
                )}

                {(showSingleCustomBadge && metaobjects?.length > 0) && renderMetaBadges(metaobjects)}
                
                {showSaleBadge && product.listPrice && product.listPrice > product.price && (
                    <span class="text-t-brand-foreground-secondary bg-t-background flex justify-center items-center caption font-[350] p-2xs rounded-sm lg:text-body lg:font-[400] lg:p-xs">
                        Sale
                    </span>
                )}
            </div>
            <div class="flex absolute top-0 right-0 p-xs">
                <button style='display:block!important;' aria-label="Add to Wishlist" data-with-epi="true" class={`swym-button swym-add-to-wishlist-view-product product_${product.productId}`} data-swaction="addToWishlist" data-variant-id={`${product.skus[0].id}`} data-product-id={`${product.productId}`} data-product-url={`${product.url}`}></button>
            </div>
        </SerpElement>
    )
}
